import { NotificationIconType, type NotificationQueryParams } from '@promotion/types'
import { PresentationType, MessageType, NotificationChannel, type MessagesPaths as paths, type MessagesComponents as components, type MessageCategory, type MessagesOperations as operations } from '@core/types'
import { useQuery, useApi, useUserNotificationsSubscriber, whenever, computed, navigateTo, ref, useDayjs, useDevice, useClock, openPopupWindow, useOpenapi, useCoreConfig, useNuxtData, useMutation, useWebauthn } from '#imports'

export const useUserNotifications = (options?: NotificationQueryParams) => {
    const client = useOpenapi<paths>({
        baseUrl: '/gateway/message/api',
        service: 'message',
    })

    const { site } = useCoreConfig()

    const api = useApi({ service: 'message' })
    const { clock } = useClock()
    const dayjs = useDayjs()
    const { isMobile } = useDevice()
    const { isMobileApp } = useWebauthn()

    const baseNavigationQueryKey = 'user:notifications-navigation' // Static key for navigation - will be used to access the query when reading notifications on the message page
    const baseNavigationData = useNuxtData(baseNavigationQueryKey)

    const { lazy = true, enabled = true, limit = 100, offset = 0, category = null, exclude_expired = false, exclude_read = false, presentation_type, useBaseQueryKey = false } = options ?? {}

    const errors = ref<Record<string, boolean>>({})

    const queryKey = options?.key ?? computed(() => useBaseQueryKey ? baseNavigationQueryKey : `user:notifications-${offset}-${limit}${exclude_read ? '-exclude_read' : ''}${exclude_expired ? '-exclude_expired' : ''}${category ? '-' + category : ''}`)

    const queryParams = computed<operations['user.notification.get']['parameters']['query']>(() => {
        return {
            offset,
            limit,
            ...(exclude_read && { exclude_read: 1 }),
            ...(exclude_expired && { exclude_expired: 1 }),
            ...(category && { category: category as components['schemas']['MessageCategory'] | null }),
            ...(presentation_type && { presentation_type: presentation_type as components['schemas']['PresentationType'] | null }),
            brand: site.brand as components['schemas']['Brand'],
            channel: isMobileApp ? NotificationChannel.app : NotificationChannel.any
        }
    })

    const compareNotifications = function (a: components['schemas']['RenderedNotification'], b: components['schemas']['RenderedNotification']) {
        // Compare read state - unread has higher priority
        if (a.read !== b.read) {
            return a.read ? 1 : -1 // unread comes before read
        }

        // If both are unread - compare priority
        if (!a.read && a.priority !== b.priority) {
            return (b?.priority ?? 0) - (a.priority ?? 0)
        }

        // If priority is the same, compare displayed_created_at/created_at
        return dayjs(b.displayed_created_at ?? b.created_at).tz().diff(dayjs(a.displayed_created_at ?? a.created_at).tz())
    }

    const query = useQuery(async () => {
        const { data } = await client.GET('/v1/public/notifications', { params: { query: queryParams.value as typeof queryParams.value & { category: MessageCategory } } })

        if (data?.notifications?.length) {
            data.notifications.sort(compareNotifications)
        }

        return data
    }, {
        key: queryKey,
        lazy,
        server: !lazy,
        enabled: enabled,
        deep: true,
    })

    const userNotifications = computed(() => {
        if (!query.data.value?.notifications) return []

        return query.data.value.notifications.filter(notification => notification.presentation_type === PresentationType.notification)
    })

    const unreadNotificationsCount = computed(() => {
        return userNotifications.value.filter(notification => !notification.read).length
    })

    const dialogs = computed(() => {
        if (!query.data.value?.notifications) return []

        return query.data.value.notifications.filter(notification =>
            notification.presentation_type === PresentationType.dialog
            || notification.presentation_type === PresentationType.persistent_dialog)
    })

    const flashes = computed(() => {
        if (!query.data.value?.notifications) return []

        return query.data.value.notifications.filter(notification =>
            notification.presentation_type === PresentationType.flash
            && !notification.read)
    })

    function useCallAction() {
        return useMutation(async (body: {
            action: components['schemas']['RenderedNotificationAction']
            notification: components['schemas']['RenderedNotification']
        }) => {
            const { action, notification } = body

            if (notification.id in errors.value) {
                delete errors.value[notification.id]
            }

            if (action?.callback) {
                const data = await api<components['schemas']['RenderedNotification']>(action.callback, {
                    method: 'POST'
                })

                return data
            }
        }, {
            onError(error, body) {
                const { notification } = body!

                if (error && !(notification.id in errors.value)) {
                    errors.value[notification.id] = true
                }
            },
            async onSuccess(data, body) {
                const { action, notification } = body!

                if (data) {
                    await query.refresh({ cause: 'refresh:manual' })
                }

                if (action?.redirect) {
                    if (notification!.type === MessageType.game_launch && !isMobile) {
                        openPopupWindow(action.redirect, 'play')
                    }
                    else {
                        await navigateTo(action.redirect, {
                            external: true, // Mark it external, since it could be a link to playgame, some page in the user section etc
                        })
                    }
                }
            }
        })
    }

    const markAsRead = useMutation(async (body: {
        notifications: components['schemas']['RenderedNotification'][]
        silent?: boolean
    }) => {
        const { notifications } = body

        const ids = notifications.map(notification => notification.id)

        await client.POST('/v1/public/notifications/read', {
            body: {
                notificationIds: ids
            },
            parseAs: 'text' // There is no response, so we don't want to try and parse it as json, since it will fail and avoid triggering the onSuccess callback
        })

        return ids
    }, {
        async onSuccess(notificationIds, body) {
            if (body?.silent) return

            // Dialogs expect the notification to be removed if it has been read, so refresh instead of optimistic update.
            const isDialog = body?.notifications.some(notification => notification.presentation_type === PresentationType.dialog || notification.presentation_type === PresentationType.persistent_dialog)

            if (isDialog) {
                await query.refresh()
                return
            }

            if (query.data?.value?.notifications) {
                const readUpdate = { read: true, read_at: dayjs(clock.value).tz().toISOString() }

                for (const id of notificationIds) {
                    query.data.value?.notifications?.forEach((n: components['schemas']['RenderedNotification'], index: number) => {
                        if (!query.data?.value?.notifications?.length) return

                        if (n.id === id) {
                            query.data.value.notifications[index] = { ...n, ...readUpdate }
                        }
                    })

                    // Also update baseData notifications (e.g., those used in navigation) so that the unread indicator
                    // is removed when all notifications have been marked as read.
                    const baseData = baseNavigationData?.data?.value
                    baseData?.notifications?.forEach((n: components['schemas']['RenderedNotification'], index: number) => {
                        if (n.id === id) {
                            baseData.notifications[index] = { ...n, ...readUpdate }
                        }
                    })
                }
            }
        }
    })

    const markAsShown = useMutation((body: components['schemas']['RenderedNotification']) => {
        return client.POST('/v1/public/notifications/{notificationId}/shown', {
            params: {
                path: {
                    notificationId: body.id
                }
            }
        })
    })

    const typeToIconMap: { [key in MessageType]?: NotificationIconType } = {
        [MessageType.promotion_recurring_consent]: NotificationIconType.Thermometer,
        [MessageType.source_of_funds_request]: NotificationIconType.Notification,
        [MessageType.game_launch]: NotificationIconType.Game,
        [MessageType.withdrawal_request_approved]: NotificationIconType.Withdrawal
    }

    function getIcon(type: MessageType | string): NotificationIconType {
        return typeToIconMap[type as MessageType] || NotificationIconType.Notification
    }

    if (import.meta.client) {
        const { notification } = useUserNotificationsSubscriber()

        whenever(notification, (notificationSubscribeResult: components['schemas']['RenderedNotification']) => {
            query.data.value ||= {
                notifications: []
            }
            query.data.value.notifications ||= []

            const notificationIdx = query.data.value.notifications.findIndex(x => x.id === notificationSubscribeResult.id)

            if (notificationIdx !== -1) {
                query.data.value.notifications.splice(notificationIdx, 1, notificationSubscribeResult)

                return
            }

            query.data.value.notifications.unshift(notificationSubscribeResult)
        }, {
            immediate: true,
        })
    }

    return query.enrich({
        userNotifications,
        unreadNotificationsCount,
        dialogs,
        flashes,
        useCallAction,
        markAsRead,
        markAsShown,
        errors,
        getIcon
    })
}
